import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, ValidationOptions } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import cn from 'classnames';
import './input.styles.scss';
import 'react-datepicker/dist/react-datepicker.css';
import './datePicker.style.scss';
import { validateRequired, ISO_DATE_FORMAT } from 'utils';
import { CalendarIcon } from '../../icons';

interface SingleDatePickerFieldProps {
	id: string;
	label: string;
	required?: boolean;
	error?: string;
	hintText?: string;
	name: string;
	monthsShown?: number;
	disabled?: boolean;
	value?: string;
	readOnly?: boolean;
	minDate?: Date | null;
	validation?: ValidationOptions;
}

export const DatePickerField = ({
	id,
	label,
	required = false,
	error,
	hintText,
	name,
	monthsShown = 1,
	value,
	disabled = false,
	readOnly = false,
	minDate = null,
	validation = {},
}: SingleDatePickerFieldProps) => {
	const { getValues, control, errors, clearError } = useFormContext();
	const { t } = useTranslation('errors');
	const pickerRef = useRef<DatePicker>(null);

	const [isFocused, setIsFocused] = useState<boolean | null>(false);

	const handleFocus = () => {
		setIsFocused(true);
	};

	const handleIconClick = useCallback(() => pickerRef?.current?.setOpen(true), [
		pickerRef,
	]);

	const fieldError = useMemo(
		() =>
			errors[name]?.types?.message ||
			errors[name]?.message ||
			errors[name] ||
			error,
		[error, errors, name],
	);

	return (
		<div
			className={cn(
				'group group--datepicker row',
				getValues()[name] ? 'group--filled' : 'group--empty',
				{
					'group--active': isFocused,
				},
			)}
		>
			<div onClick={handleIconClick}>
				<CalendarIcon iconClass="group__icon" />
			</div>
			<Controller
				control={control}
				name={name}
				rules={{
					...validateRequired(required, t('required')),
					...validation,
				}}
				onClick={handleFocus}
				onChange={([selected]) =>
					selected && moment(selected).format(ISO_DATE_FORMAT)
				}
				valueName={name}
				as={({ onChange }) => (
					<DatePicker
						ref={pickerRef}
						selected={getValues()[name] ? new Date(getValues()[name]) : null}
						onChange={(e) => {
							if (
								errors?.contractDto?.endDate ||
								errors?.contractDto?.startDate
							) {
								clearError('contractDto');
							}
							onChange(e);
						}}
						className={cn(
							'group__datepicker',
							errors[name] || error ? 'group__datepicker_error' : '',
						)}
						monthsShown={monthsShown}
						autoComplete="off"
						dateFormat="yyyy/MM/dd"
						strictParsing
						disabled={disabled}
						readOnly={readOnly}
						minDate={minDate}
						shouldCloseOnSelect
						popperModifiers={{
							preventOverflow: {
								enabled: true,
							},
						}}
					/>
				)}
			/>
			<div
				className="group__label-bg"
				data-label={required ? `${label}*` : label}
			/>
			{!value && (
				<label className="group__label" htmlFor={id}>
					{required ? `${label}*` : label}
				</label>
			)}
			<p
				className={cn('group__hint font-caption', {
					'group__hint--error': !!fieldError,
				})}
			>
				{fieldError || hintText}
			</p>
		</div>
	);
};
